import { useEffect, useState } from "react";
import { Menu, MenuItem, ListItemIcon } from "../../../../components";
import HistoricoPagamentos from "./historico-pagamentos.component";
import { formatDate, formatPrice, responseErros } from "../../../../utils";
import { format } from "date-fns";
import { getAtrasoDias } from "../add-contas-receber";
import shortid from "shortid";
import { BoletosHelper } from "../../../../helpers/boletos.helper";
import {
  statusValidosBaixa,
  statusValidosConsulta,
  statusValidosDescarte,
  statusValidosInclusao,
} from "../../resources/constantes";
import {
  BoletoService,
  notification,
  PlugBoletoService,
} from "../../../../services";
import EnvioWhatsappDialog from "../../list-contas-receber/components/boleto/envio-whatsapp.dialog";

const MenuAddActions = ({
  anchorEl,
  setAnchorEl,
  conta,
  dadosPagamentoList,
  dadosEmpresa,
  listaMeiosPagamentos,
  listaContas,
  pagadorList,
  setReciboData,
  handlePrint,
  buscarContaReceber,
  validarInclusaoBoleto,
  meioPagamento,
  setLoading,
}) => {
  const plugBoletoService = new PlugBoletoService();
  const boletoService = new BoletoService();
  const boletosHelper = new BoletosHelper();

  const [openHistorico, setOpenHistorico] = useState(false);
  const [openWhatsappDialog, setOpenWhatsappDialog] = useState(false);
  const [boleto, setBoleto] = useState(conta?.boleto);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = "primary-add-actions-menu";
  const dadosPagamentoColunas = [
    {
      field: "dataPagamento",
      headerName: "Data",
      flex: 400,
      valueGetter: (params) => {
        return format(formatDate.received(params.value), "dd/MM/yyyy");
      },
    },
    {
      field: "horaPagamento",
      headerName: "Hora",
      flex: 250,
      valueGetter: (params) => {
        return format(formatDate.received(params.value), "HH:mm");
      },
    },
    {
      field: "bancoCaixaId",
      headerName: "Banco/Caixa",
      flex: 400,
      valueGetter: (params) => {
        const conta = listaContas.find((item) => item.id == params.value);
        if (!conta) return params.value;
        return conta.nome;
      },
    },
    {
      field: "atrasoDias",
      headerName: "Atraso",
      flex: 350,
      valueGetter: (params) => {
        const dataPagamento = params.row.dataPagamento;
        return getAtrasoDias(conta?.dataVencimento, dataPagamento);
      },
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio Pagamento",
      flex: 600,
      valueGetter: (params) => {
        const meioPagamento = listaMeiosPagamentos.find(
          (meio) => meio.id == params.value,
        );
        if (!meioPagamento) return params.value;
        return meioPagamento.descricao;
      },
    },
    {
      field: "tipoBaixa",
      headerName: "Tipo de Baixa",
      flex: 600,
      renderCell: (params) => {
        switch (params.value) {
          case "BAIXA_RENEGOCIACAO":
            return "Baixa por Renegociação";
          case "BAIXA_COMPENSACAO":
            return "Baixa por Compensação";
          default:
            return "Baixa Normal";
        }
      },
    },
    {
      field: "valor",
      headerName: "Valor Baixa",
      flex: 450,
      valueGetter: (params) => formatPrice(parseFloat(params.value)),
    },
    {
      field: "insertUserName",
      headerName: "Usuário",
      flex: 450,
    },
  ];

  useEffect(() => {
    const cliente = pagadorList.find((item) => item.id == conta.pagadorId);
    setReciboData({
      cliente,
      titulo: conta,
      pagamentos: dadosPagamentoList,
    });
  }, [conta, pagadorList]);

  const handleAddActionsClose = () => {
    setAnchorEl(null);
  };

  const handleHistorico = () => {
    setOpenHistorico(!openHistorico);
    handleAddActionsClose();
  };

  async function enviarEmail() {
    const cliente = pagadorList.find((item) => item.id == conta.pagadorId);
    if (cliente?.email) {
      try {
        setLoading(true);
        await boletosHelper.enviarEmail(conta.boleto, cliente, dadosEmpresa);
      } catch (error) {
        notification.alertaGenericos(
          error?.message ?? "Erro interno do servidor",
        );
      } finally {
        setLoading(false);
      }
    } else {
      notification.alertaGenericos("Este cliente não possui e-mail cadastrado");
    }
  }

  async function descartarBoleto(id, integradorId) {
    setLoading(true);
    const result = await plugBoletoService.descartarEmLote(
      [integradorId],
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (result?._dados?._sucesso?.length) {
      const updateBody = [{ id, status: "DESCARTADO" }];
      const resultDelete = await boletoService.update(updateBody);
      if (!resultDelete.isAxiosError) {
        notification.sucessoGenericos("Boleto descartado com sucesso");
        await buscarContaReceber();
      } else {
        responseErros(resultDelete);
      }
    } else {
      notification.alertaGenericos("Erro ao descartar boleto");
    }
    setLoading(false);
  }

  async function baixarBoleto(integradorId) {
    setLoading(true);

    const result = await plugBoletoService.baixarEmLote(
      [integradorId],
      dadosEmpresa.empresa.cnpjCpf,
    );
    if (result?._dados?.situacao) {
      notification.sucessoGenericos(
        "Boleto encaminhado para processamento de baixa",
      );
    } else {
      notification.alertaGenericos("Erro ao processar baixa");
    }

    setLoading(false);
  }

  const acoesGerarBoleto = meioPagamento.gerarBoleto
    ? [
        <MenuItem
          className="mb-1 mt-3"
          disabled={
            conta.boleto && !statusValidosInclusao.includes(conta.boleto.status)
          }
          onClick={validarInclusaoBoleto}
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-barcode" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Gerar Boleto</span>
        </MenuItem>,
      ]
    : [];

  const acoesBoleto = conta.boleto
    ? [
        <MenuItem
          className="mb-1 mt-3"
          disabled={!statusValidosConsulta.includes(conta.boleto.status)}
          onClick={async () => {
            setLoading(true);
            await boletosHelper.imprimirBoleto(
              conta.boleto.integradorImpressaoId,
              dadosEmpresa,
            );
            setLoading(false);
          }}
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-file-pdf" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Imprimir Boleto</span>
        </MenuItem>,
        <MenuItem
          className="mb-1 mt-3"
          disabled={!statusValidosConsulta.includes(conta.boleto.status)}
          onClick={async (event) => {
            event.stopPropagation();
            setLoading(true);
            await enviarEmail();
            setLoading(false);
          }}
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-envelope" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Enviar Email</span>
        </MenuItem>,
        <MenuItem
          className="mb-1 mt-3"
          disabled={!statusValidosConsulta.includes(conta.boleto.status)}
          onClick={async () => {
            setLoading(true);
            const cliente = pagadorList.find(
              (item) => item.id == conta.pagadorId,
            );
            setBoleto({ ...conta?.boleto, cliente });
            setOpenWhatsappDialog(true);
            setLoading(false);
          }}
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-whatsapp-logo" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Enviar WhatsApp</span>
        </MenuItem>,
        <MenuItem
          className="mb-1 mt-3"
          disabled={!statusValidosDescarte.includes(conta.boleto.status)}
          onClick={() =>
            descartarBoleto(conta.boleto.id, conta.boleto.integradorId)
          }
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-trash" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Cancelar Emissão</span>
        </MenuItem>,
        <MenuItem
          className="mb-2 mt-3"
          disabled={!statusValidosBaixa.includes(conta.boleto.status)}
          onClick={() => baixarBoleto(conta.boleto.integradorId)}
          sx={{ height: 20, borderRadius: 5 }}
          key={shortid.generate()}
        >
          <ListItemIcon style={{ minWidth: 42 }}>
            <i className="ph ph-file-x" style={{ fontSize: 16 }}></i>
          </ListItemIcon>
          <span style={{ fontSize: 12 }}>Cancelar Registro</span>
        </MenuItem>,
      ]
    : [];

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        elevation={1}
        getContentAnchorEl={null}
        id={menuId}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        autoFocus={false}
        open={isMenuOpen}
        onClose={handleAddActionsClose}
        className="m-3"
      >
        {[
          <MenuItem
            className="my-1 pe-4"
            onClick={handleHistorico}
            disabled={!dadosPagamentoList.length}
            sx={{ height: 20, borderRadius: 5 }}
            key={shortid.generate()}
          >
            <ListItemIcon style={{ minWidth: 42 }}>
              <i className="ph ph-invoice" style={{ fontSize: 16 }}></i>
            </ListItemIcon>
            <span style={{ fontSize: 12 }}>Histórico Pagamentos</span>
          </MenuItem>,
          <MenuItem
            className="mb-1 mt-3"
            onClick={handlePrint}
            disabled={
              conta?.status !== "BAIXADO_PARCIAL" && conta?.status !== "BAIXADO"
            }
            sx={{ height: 20, borderRadius: 5 }}
            key={shortid.generate()}
          >
            <ListItemIcon style={{ minWidth: 42 }}>
              <i className="ph ph-printer" style={{ fontSize: 16 }}></i>
            </ListItemIcon>
            <span style={{ fontSize: 12 }}>Imprimir Recibo</span>
          </MenuItem>,
          ...acoesGerarBoleto,
          ...acoesBoleto,
        ]}
      </Menu>
      <HistoricoPagamentos
        dadosPagamentoList={dadosPagamentoList}
        dadosPagamentoColunas={dadosPagamentoColunas}
        openHistorico={openHistorico}
        handleHistorico={handleHistorico}
      />
      <EnvioWhatsappDialog
        open={openWhatsappDialog}
        setOpen={setOpenWhatsappDialog}
        boleto={boleto}
        dadosEmpresa={dadosEmpresa}
      />
    </>
  );
};

export default MenuAddActions;
